import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Spinner,
  useIsMobile,
  useToast,
} from '@dotfile/frontend/shared/design-system';

import { useYousignSdk } from '../../../../shared/contexts/yousign-sdk';
import {
  ELECTRONIC_SIGNATURE_IFRAME_HEIGHT,
  ELECTRONIC_SIGNATURE_IFRAME_HEIGHT_MOBILE,
} from '../constants';
import { VendorIframeProps } from '../types';

// Pure component to not rerender if url don't change
// Make sure you wrap handlers in useCallback to not trigger rerender
export const YousignIframe = memo(function YousignIframe({
  signatureLink,
  onComplete,
}: VendorIframeProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const isMobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const { loaded: sdkLoaded } = useYousignSdk();
  const yousignInstanceRef = useRef<{
    destroy?: () => void;
  } | null>(null);

  useEffect(() => {
    if (!signatureLink || !ref.current || !sdkLoaded || !window.Yousign) return;

    try {
      // Initialize Yousign iframe using global SDK
      const yousign = new window.Yousign({
        signatureLink,
        iframeContainerId: 'yousign-iframe-container',
        isSandbox: false,
        iframeAttributes: {
          referrerPolicy: 'origin-when-cross-origin',
        },
      });

      // Listen to success event
      yousign.onSuccess(onComplete);

      // Store instance in ref for cleanup
      yousignInstanceRef.current = yousign;
    } catch (error) {
      toast({
        title: t('checks.electronic_signature.error_title', {
          defaultValue: 'Error',
          ns: 'client-portal',
        }),
        description: t('checks.electronic_signature.error_description', {
          defaultValue:
            'Failed to initialize the signature interface. Please try again.',
          ns: 'client-portal',
        }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    // Cleanup function
    return () => {
      if (
        yousignInstanceRef.current &&
        typeof yousignInstanceRef.current.destroy === 'function'
      ) {
        yousignInstanceRef.current.destroy();
      }
      yousignInstanceRef.current = null;
    };
  }, [signatureLink, onComplete, sdkLoaded, toast, t]);

  // Show loading spinner while SDK is loading
  if (!sdkLoaded) {
    return (
      <Box
        minHeight={
          isMobile
            ? ELECTRONIC_SIGNATURE_IFRAME_HEIGHT_MOBILE
            : ELECTRONIC_SIGNATURE_IFRAME_HEIGHT
        }
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="xl" color="custom.500" />
      </Box>
    );
  }

  return (
    <Box
      minHeight={
        isMobile
          ? ELECTRONIC_SIGNATURE_IFRAME_HEIGHT_MOBILE
          : ELECTRONIC_SIGNATURE_IFRAME_HEIGHT
      }
      width="100%"
      display="flex"
      flex="1"
      ref={ref}
      id="yousign-iframe-container"
      sx={{
        ' & iframe': {
          width: '100%',
        },
      }}
    />
  );
});
